/**
 * Returns a progressive relative time label and a tooltip title for a date string.
 *
 * Labels:
 *  < 1 min   → "just now"
 *  1–59 min  → "12m ago"
 *  1–23 hr   → "3h ago"
 *  1–6 days  → "2d ago"
 *  7+ days, same year → "Mar 14"
 *  different year     → "Mar 14, 2025"
 *
 * Tooltip: full locale string, e.g. "March 14, 2025 at 09:31 AM"
 */
export function timeAgo(dateStr: string | undefined | null): { label: string; title: string } {
  if (!dateStr) return { label: '—', title: '' };

  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();

  const title = date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return { label: 'just now', title };
  if (mins < 60) return { label: `${mins}m ago`, title };

  const hours = Math.floor(mins / 60);
  if (hours < 24) return { label: `${hours}h ago`, title };

  const days = Math.floor(hours / 24);
  if (days < 7) return { label: `${days}d ago`, title };

  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  const label = date.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });

  return { label, title };
}
